import rung from "rung/src/rung";
import Tiles from "../tiles";
import { fillConnectedAreas, getAdjacentCells } from "./flow";
import { createPathfinder } from "./pathfinder";

const rng = rung();

const defaultConfig = {
  width: 70,
  height: 180
}

const TerrainCell = {
  CHASM: 0,
  WALL: 1,
  FLOOR: 2,
  ENTRANCE: 3,
  EXIT: 4
}

function generateFilledTerrain(width, height) {
  const grid = [];

  for (let y=0; y < height; y++) {
    grid[y] = [];
    for (let x=0; x < width; x++) {
      if ((x < 2 || x >= width-3) || (y < 2 || y >= height-3)) {
        grid[y][x] = TerrainCell.WALL;
      } else {
        grid[y][x] = TerrainCell.FLOOR;
      }
    }
  }

  return grid;
}

function generateForest(configuration) {
  const config = { ...defaultConfig, ...configuration };
  const width = config.width;
  const height = config.height;

  const terrain = generateFilledTerrain(width, height);

  for (let i=0; i<300; i++) {
    const x = rng.integer(0, width);
    const y = rng.integer(0, height);

    terrain[y][x] = TerrainCell.WALL;
  }

  splatContours(width, height, terrain);

  // Generate the entrance and exit before connecting areas so we can ensure the
  // level is entirely traversable
  const entrance = {
    x: rng.integer(3, width-3),
    y: height-3
  }

  const exit = {
    x: rng.integer(3, width-3),
    y: 4
  }

  terrain[entrance.y - 1][entrance.x - 1] = TerrainCell.FLOOR;
  terrain[entrance.y - 1][entrance.x] = TerrainCell.FLOOR;
  terrain[entrance.y - 1][entrance.x + 1] = TerrainCell.FLOOR;

  terrain[exit.y + 1][exit.x - 1] = TerrainCell.FLOOR;
  terrain[exit.y + 1][exit.x] = TerrainCell.FLOOR;
  terrain[exit.y + 1][exit.x + 1] = TerrainCell.FLOOR;

  // Flood fill again to detect open cave areas
  const areas = fillConnectedAreas(terrain, TerrainCell.FLOOR);

  // Sort the areas first so that we can fill small gaps before doing a larger pass.
  areas.sort((a, b) => a.length - b.length);

  const loopholes = [];
  const caves = [];

  for (const area of areas) {
    caves.push(area);
  }

  // Ensure main cave areas are continuously navigable.
  for (let i = 0; i < caves.length - 1; i++) {

    // Callback to collect adjacent nodes from the terrain map
    const adjacentNodes = (x, y) => {
      const adjacent = [];
      for (const node of getAdjacentCells(width, height, x, y)) {
        adjacent.push(node);
      }
      return adjacent;
    }

    // TODO: document this in flatland
    const costHeuristic = (from, to) => (
      Math.abs(from.x - to.x) + Math.abs(from.y - to.y) * 1.01
    );

    // Wall tiles are more costly than floor tiles but can still be traversed
    // Tweak this to see what different kinds of pathways are possible.
    const traversalCost = (x, y) => {
      return (terrain[y][x] == TerrainCell.WALL) ? 4 : 1;
    }

    // Can move through rock and caves but chasm is off limits
    const isTraversable = (x, y) => terrain[y][x] != TerrainCell.CHASM;

    // Configure an A* search to trace a path from source to target
    const search = createPathfinder(
      adjacentNodes,
      costHeuristic,
      traversalCost,
      isTraversable
    );

    const source = caves[i][rng.integer(0, caves[i].length)];
    const target = caves[i + 1][rng.integer(0, caves[i + 1].length)];

    const path = search(source, target);

    // Fill the entire path in with cave tiles
    for (const connectingCell of path) {
      terrain[connectingCell.y][connectingCell.x] = TerrainCell.FLOOR;
    }
  }

  const tiles = [];

  const forestTrees = [
    Tiles.Forest1,
    Tiles.Forest2
  ];

  const forestFloor = [
    Tiles.ForestFloor1,
    Tiles.ForestFloor2,
    Tiles.ForestFloor3,
    Tiles.ForestFloor4,
    Tiles.ForestFloor5,
    Tiles.ForestFloor6
  ]

  for (let y=0; y < height; y++) {
    tiles[y] = [];
    for (let x=0; x < width; x++) {
      switch (terrain[y][x]) {
        case TerrainCell.WALL:
          tiles[y][x] = forestTrees[rng.integer(0, 2)];
          //tiles[y][x] = Tiles.Forest1;
        break;
        case TerrainCell.FLOOR:
          tiles[y][x] = forestFloor[rng.integer(0, 6)];
        break;
      }
    }
  }

  // Now that we now things are connected and we have filled out all the tiles
  // place the entrance portal
  tiles[entrance.y][entrance.x] = Tiles.ForestEntrance;
  tiles[exit.y][exit.x] = Tiles.ForestExit;

  return {
    tiles,
    portals: {
      entrance,
      exit
    },
    features: {}
  }
}

function splatContours(worldWidth, worldHeight, world, use_borders = false) {

  var min_x = 0;
  var min_y = 0;
  var max_x = worldWidth;
  var max_y = worldHeight;

  var candidates = [];

  // create list of tile candidates
  for (var y=0; y < worldHeight; y++) {
      for (var x=0; x < worldWidth; x++)  {
      if(world[y][x] != TerrainCell.FLOOR) {
        candidates.push({x,y});
      }
    }
  }

  // shuffle them all
  for ( var p = 0; p < candidates.length; p++) {
    var off = rng.integer(candidates.length + 1);
    candidates[p] = [candidates[off],candidates[off]=candidates[p]][0];
  }

  var iteration_number = candidates.length * 4.8;

  for (var k = 0; k < iteration_number; k++) {
    let random_offset = 0;
    let tile_variation = 0;

    // 1/3 chance that we will use as a bombing point one of the last 15 positions
    if (rng.integer(3+1) == 0) {
      random_offset = rng.integer(candidates.length - 14, candidates.length);
      tile_variation = TerrainCell.WALL;
    } else {
      // otherwise use lower half of remaining tiles
      random_offset = rng.integer((candidates.length/2) + 1);
      tile_variation = TerrainCell.WALL;
    }

    // check boundaries
    if (random_offset >= candidates.length) {
      random_offset = candidates.length - 1;
    }

    if (random_offset < 0) {
      random_offset = 0;
    }

    if (!candidates[random_offset]) continue;

    var tx = candidates[random_offset].x;
    var ty = candidates[random_offset].y;
    var map_width = worldWidth;
    var map_height = worldHeight;
    var use_borders = true;

     // we will use bombs of radius 1 mostly with smaller chance (1/20)
     // that radius will be of size 2
     var bomb_radius = rng.integer(20+1) != 0 ? 1 : 2;

      // bomb
      for (var x = Math.max(0, tx - bomb_radius - 1); x < Math.max(map_width, tx + bomb_radius); x++) {
      for (var y = Math.max(0, ty - bomb_radius - 1); y < Math.max(map_height, ty + bomb_radius); y++) {

        // check if tile is within the circle
        if ((x - tx)*(x - tx) + (y - ty)*(y - ty) < bomb_radius * bomb_radius +  bomb_radius) {

            if (use_borders) {
              if (x < min_x) continue;
              if (x >= max_x) continue;
              if (y < min_y) continue;
              if (y >= max_y) continue;
            }

            // if we have at least one tile bombed on screen
            // push those coordinates to candidate list
            if (world[y][x] != tile_variation)   {
              world[y][x] = tile_variation;
              candidates.push({x, y});
            }
         }
      }
    }

    // erase our bombing cell, it is re-added in bombing loop above, if at least one tile is changed.
    candidates.splice(random_offset,1);
  }

  return true;
}

export {
  generateForest
}
